import { useMemo, useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { submitApplication } from '../services/api';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidHttpsUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function normalizeMusicUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/\s/.test(trimmed)) return null;

  const withProtocol = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  try {
    const parsed = new URL(withProtocol);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    if (!parsed.hostname || !parsed.hostname.includes('.')) return null;

    if (parsed.protocol === 'http:') {
      parsed.protocol = 'https:';
    }

    return parsed.toString();
  } catch {
    return null;
  }
}

function normalizeOptionalWebUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return normalizeMusicUrl(trimmed);
}

function readSourceRefFromUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  const params = new URLSearchParams(window.location.search);
  const sourceRef =
    params.get('sourceRef') ||
    params.get('source') ||
    params.get('capture') ||
    params.get('campaign') ||
    params.get('ref') ||
    '';

  const trimmed = sourceRef.trim();
  if (!trimmed) return undefined;

  if (trimmed.length > 120) return undefined;
  if (!/^[a-zA-Z0-9:_-]+$/.test(trimmed)) return undefined;

  return trimmed;
}

export default function RequestAccessPage() {
  const sourceRef = useMemo(() => readSourceRefFromUrl(), []);

  const [form, setForm] = useState({
    artistName: '',
    email: '',
    primaryMusicLink: '',
    socialProfileUrl: '',
    note: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const trimmedArtistName = form.artistName.trim();
  const trimmedEmail = form.email.trim().toLowerCase();
  const normalizedPrimaryMusicLink = normalizeMusicUrl(form.primaryMusicLink);
  const normalizedSocialProfileUrl = normalizeOptionalWebUrl(form.socialProfileUrl);
  const noteLength = form.note.length;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    setError(null);

    if (!trimmedArtistName) {
      setError('Artist or band name is required.');
      return;
    }

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setError('Enter a valid email address.');
      return;
    }

    if (!normalizedPrimaryMusicLink) {
      setError('Please enter a valid link to your music.');
      return;
    }

    if (form.socialProfileUrl.trim() && !normalizedSocialProfileUrl) {
      setError('Social profile must be a valid link.');
      return;
    }

    if (noteLength > 1000) {
      setError('Anything you\'d like us to know? must be 1000 characters or fewer.');
      return;
    }

    setSubmitting(true);

    try {
      const response = await submitApplication({
        artistName: trimmedArtistName,
        email: trimmedEmail,
        primaryMusicLink: normalizedPrimaryMusicLink,
        socialProfileUrl: normalizedSocialProfileUrl || undefined,
        note: form.note.trim() || undefined,
        sourceRef,
      });

      const data = (await response.json()) as { error?: string };
      if (!response.ok) {
        setError(data.error || 'Failed to submit application.');
        return;
      }

      setSuccess(true);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center px-4 py-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-8 sm:p-10 max-w-xl w-full text-center"
        >
          <div className="flex items-center justify-center gap-2 mb-6">
            <img src="/fans-audio-logo.png" alt="fans.audio logo" className="w-8 h-8 object-contain" />
            <span className="font-mono font-bold text-lg">fans<span className="text-electric-cyan">.audio</span></span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Your application is in.</h2>
          <p className="text-text-secondary text-lg leading-relaxed mb-8">
            Thanks for your interest in fans.audio.
            <br />
            We're welcoming artists gradually as we build the platform. We'll review your application and get in touch by email.
          </p>
          <a href="/" className="btn-secondary inline-flex items-center">
            Back to Home
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-charcoal flex items-center justify-center px-4 py-16">
      <div className="absolute inset-0 waveform-bg opacity-30" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-electric-cyan/5 rounded-full blur-[120px]" />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="relative z-10 w-full max-w-2xl"
      >
        <motion.div variants={fadeInUp} className="text-center mb-8">
          <a href="/" className="inline-flex items-center justify-center gap-2 mb-6">
            <img src="/fans-audio-logo.png" alt="fans.audio logo" className="w-8 h-8 object-contain" />
            <span className="font-mono font-bold text-lg">fans<span className="text-electric-cyan">.audio</span></span>
          </a>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">Bring your music home.</h1>
          <p className="text-text-secondary text-lg leading-relaxed max-w-xl mx-auto">
            We're welcoming independent artists gradually as we build fans.audio.
            <br />
            Tell us a little about yourself and your music.
          </p>
        </motion.div>

        <motion.form variants={fadeInUp} onSubmit={handleSubmit} className="glass-card p-6 sm:p-8 space-y-5">
          <div>
            <label htmlFor="artistName" className="block text-sm font-medium text-white mb-2">Artist or band name</label>
            <input
              id="artistName"
              type="text"
              value={form.artistName}
              onChange={(event) => setForm((prev) => ({ ...prev, artistName: event.target.value }))}
              placeholder="The name you release music under"
              className="w-full px-4 py-3 bg-surface border border-white/10 rounded-lg text-white placeholder-text-muted focus:border-electric-cyan/50 focus:outline-none focus:ring-1 focus:ring-electric-cyan/30 transition-colors"
              required
              autoComplete="organization"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-white mb-2">Email address</label>
            <input
              id="email"
              type="email"
              value={form.email}
              onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
              placeholder="you@example.com"
              className="w-full px-4 py-3 bg-surface border border-white/10 rounded-lg text-white placeholder-text-muted focus:border-electric-cyan/50 focus:outline-none focus:ring-1 focus:ring-electric-cyan/30 transition-colors"
              required
              autoComplete="email"
            />
          </div>

          <div>
            <label htmlFor="primaryMusicLink" className="block text-sm font-medium text-white mb-2">Where can we hear your music?</label>
            <p className="text-xs text-text-muted mb-2">Link to your music on Spotify, Bandcamp, SoundCloud, YouTube or your own website.</p>
            <input
              id="primaryMusicLink"
              type="url"
              value={form.primaryMusicLink}
              onChange={(event) => setForm((prev) => ({ ...prev, primaryMusicLink: event.target.value }))}
              onBlur={() => {
                const normalized = normalizeMusicUrl(form.primaryMusicLink);
                if (normalized && normalized !== form.primaryMusicLink) {
                  setForm((prev) => ({ ...prev, primaryMusicLink: normalized }));
                }
              }}
              placeholder="https://..."
              className="w-full px-4 py-3 bg-surface border border-white/10 rounded-lg text-white placeholder-text-muted focus:border-electric-cyan/50 focus:outline-none focus:ring-1 focus:ring-electric-cyan/30 transition-colors"
              required
            />
          </div>

          <div>
            <label htmlFor="socialProfileUrl" className="block text-sm font-medium text-white mb-2">
              Social profile <span className="text-text-muted">(optional)</span>
            </label>
            <p className="text-xs text-text-muted mb-2">Share any social profile where fans can find you.</p>
            <input
              id="socialProfileUrl"
              type="url"
              value={form.socialProfileUrl}
              onChange={(event) => setForm((prev) => ({ ...prev, socialProfileUrl: event.target.value }))}
              onBlur={() => {
                const normalized = normalizeOptionalWebUrl(form.socialProfileUrl);
                if (normalized && normalized !== form.socialProfileUrl) {
                  setForm((prev) => ({ ...prev, socialProfileUrl: normalized }));
                }
              }}
              placeholder="https://"
              className="w-full px-4 py-3 bg-surface border border-white/10 rounded-lg text-white placeholder-text-muted focus:border-electric-cyan/50 focus:outline-none focus:ring-1 focus:ring-electric-cyan/30 transition-colors"
            />
          </div>

          <div>
            <label htmlFor="note" className="block text-sm font-medium text-white mb-2">
              Anything you'd like us to know? <span className="text-text-muted">(optional)</span>
            </label>
            <textarea
              id="note"
              value={form.note}
              onChange={(event) => {
                if (event.target.value.length <= 1000) {
                  setForm((prev) => ({ ...prev, note: event.target.value }));
                }
              }}
              placeholder="Tell us a little about your music or what interests you about fans.audio."
              rows={4}
              className="w-full px-4 py-3 bg-surface border border-white/10 rounded-lg text-white placeholder-text-muted focus:border-electric-cyan/50 focus:outline-none focus:ring-1 focus:ring-electric-cyan/30 transition-colors resize-y"
            />
            <p className="text-xs text-text-muted mt-2">{noteLength}/1000</p>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20" role="alert" aria-live="polite">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full !justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Apply to Join'}
          </button>

          <p className="text-center text-sm text-text-secondary">We'll review your application and contact you by email.</p>
        </motion.form>
      </motion.div>
    </div>
  );
}
